import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { notify } from '../../../../utils/notification';
import { getSmtpConfigReq, smtpConfigErrorMessage } from '../../../../axios/request/SocietyController/CtrlSmtpConfigReqs/CtrlSmtpConfigReqs';
import { formatIstDateTime } from '../../../../utils/helpers/istDate';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const fmtDate = (v) => (v ? formatIstDateTime(v) : '—');

const Row = ({ label, children }) => (
    <div className="tw-min-w-0">
        <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-0.5">{label}</div>
        <div className="tw-text-xs tw-text-gray-800 tw-break-words">{children ?? '—'}</div>
    </div>
);

const Section = ({ title, children }) => (
    <div>
        <div className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400 tw-mb-2">{title}</div>
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-px-3 tw-py-3 tw-rounded-lg tw-border tw-border-gray-100 tw-bg-slate-50/60">
            {children}
        </div>
    </div>
);

const iconBtn = 'tw-text-gray-400 hover:tw-text-gray-600 tw-flex-shrink-0 disabled:tw-opacity-50';

// Masked until Show is clicked; the first Show fetches GET /:id for the password,
// so it's only requested when someone actually asks to see it.
const PasswordValue = ({ societyId, uuid }) => {
    const [password, setPassword] = useState(null);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const show = async () => {
        if (visible) { setVisible(false); return; }
        if (password) { setVisible(true); return; }
        setLoading(true);
        try {
            const res = await getSmtpConfigReq(societyId, uuid);
            if (res?.success === false) throw new Error(res?.message);
            const value = res?.data?.password;
            if (!value) { notify('warning', 'The server did not return the password'); return; }
            setPassword(value);
            setVisible(true);
        } catch (err) {
            notify('error', smtpConfigErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const copy = () => navigator.clipboard?.writeText(password)
        .then(() => notify('success', 'Password copied'))
        .catch(() => notify('error', 'Could not copy password'));

    return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full">
            <span className="tw-font-mono tw-break-all">{visible ? password : '••••••••'}</span>
            <button type="button" onClick={show} disabled={loading} title={visible ? 'Hide' : 'Show'} className={iconBtn}>
                <Icon icon={loading ? 'mdi:loading' : visible ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
                    className={`tw-text-sm ${loading ? 'tw-animate-spin' : ''}`} />
            </button>
            {visible && (
                <button type="button" onClick={copy} title="Copy" className={iconBtn}>
                    <Icon icon="mdi:content-copy" className="tw-text-sm" />
                </button>
            )}
        </span>
    );
};

// Read-only details of one SMTP account.
const SmtpConfigViewModal = ({ open, societyId, config, onClose, onEdit }) => {
    if (!config) return null;
    const active = config.is_active === 1;

    return (
        <Dialog open={open} onClose={onClose} TransitionComponent={Transition}
            maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
            <DialogTitle component="div" sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
            }}>
                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                    <Icon icon="solar:letter-linear" className="tw-text-base tw-text-sky-500 tw-flex-shrink-0" />
                    <div className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">{config.config_name}</div>
                    {config.is_default === 1 && (
                        <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-50 tw-text-amber-700 tw-text-[10px] tw-font-semibold">
                            <Icon icon="mdi:star" className="tw-text-[11px]" /> Default
                        </span>
                    )}
                    <span className={`tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-semibold ${active ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-500'}`}>
                        {active ? 'Active' : 'Inactive'}
                    </span>
                </div>
                <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 2, py: 2, '&.MuiDialogContent-root': { pt: 2 } }}>
                <div className="tw-flex tw-flex-col tw-gap-4">
                    <Section title="Server">
                        <Row label="SMTP host">{config.host}</Row>
                        <Row label="Port">{config.port}</Row>
                        <Row label="Encryption">{config.encryption}</Row>
                        <Row label="Username">{config.username}</Row>
                        <Row label="Password">
                            {/* Keyed so a different account (or reopening) starts masked again. */}
                            <PasswordValue key={`${config.uuid}-${open}`} societyId={societyId} uuid={config.uuid} />
                        </Row>
                    </Section>

                    <Section title="Sender">
                        <Row label="From email">{config.from_email}</Row>
                        <Row label="From name">{config.from_name}</Row>
                        <Row label="Reply-to email">{config.reply_to_email}</Row>
                    </Section>

                    <Section title="Status">
                        <Row label="Last verified">{fmtDate(config.last_verified_at)}</Row>
                        <Row label="Last error">
                            {config.last_error ? <span className="tw-text-red-500">{config.last_error}</span> : null}
                        </Row>
                        <Row label="Created">{fmtDate(config.created_at)}</Row>
                        <Row label="Updated">{fmtDate(config.updated_at)}</Row>
                    </Section>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                <button type="button" onClick={onClose}
                    className="tw-h-8 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                    Close
                </button>
                <button type="button" onClick={onEdit} style={{ marginLeft: 0 }}
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700">
                    <Icon icon="lucide:edit" className="tw-text-sm" />
                    Edit
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default SmtpConfigViewModal;
